import React, { useContext } from 'react';
import { styled } from '@mui/material/styles';
import { uniqueId } from 'lodash';
import { NavLink } from 'react-router-dom';
import { Typography } from '@mui/material';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import {
  ManagerIcon,
  TrackingIcon,
  CreativeIcon,
  ReportsIcon,
  TVIcon,
} from './icons';
import AdvertiserContext from './AdvertiserContext';
import { RoutePaths, Themes } from '@constants';

// Define types for the active list item styles
interface ActiveListItemStyle {
  background: string;
  boxShadow?: string;
  borderRadius: number;
  color?: string;
  '& svg'?: {
    fill: string;
  };
  '& span'?: {
    color: string;
  };
  '& p'?: {
    color: string;
  };
}

interface ActiveListItemStyles {
  [Themes.DEFAULT]: ActiveListItemStyle;
  [Themes.NBCU]: ActiveListItemStyle;
}

// Define types for the styled components props
interface StyledListItemProps {
  appTheme: string;
  component?: React.ElementType;
  to?: string | { pathname: string; state: { key: string } };
  button?: boolean;
  disableRipple?: boolean;
  activeClassName?: string;
}

// Define types for the component props
interface MainListItemsProps {
  showCampaignsLink?: boolean;
  showReportsLink?: boolean;
  showCreativesLink?: boolean;
  showTrackingLink?: boolean;
}

interface SecondaryListItemsProps {
  showBusinessManagerLink?: boolean;
}

const StyledList = styled(List)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2.5),
  paddingTop: theme.spacing(5),
  paddingBottom: theme.spacing(5),
}));

const activeListItemStyles: ActiveListItemStyles = {
  [Themes.DEFAULT]: {
    background: '#13171A',
    boxShadow: '0px 1px 11px 0px rgba(0, 0, 0, 0.10) inset',
    borderRadius: 10,
    color: 'white',

    '& svg': {
      fill: '#57D9AD',
    },

    '& p': {
      color: '#57D9AD',
    },
  },
  [Themes.NBCU]: {
    background: '#646669',
    borderRadius: 5,
  },
};

const StyledListItem = styled(ListItem, {
  shouldForwardProp: prop => !['appTheme'].includes(prop as string),
})<StyledListItemProps>(({ theme, appTheme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: theme.spacing(1),
  width: theme.spacing(10),
  height: theme.spacing(10),
  padding: theme.spacing(1),
  fontSize: '0.75rem',
  fontWeight: 500,

  '&.active':
    activeListItemStyles[appTheme as keyof typeof activeListItemStyles],
}));

export const MainListItems: React.FC<MainListItemsProps> = ({
  showCampaignsLink = false,
  showReportsLink = false,
  showCreativesLink = false,
  showTrackingLink = false,
}) => {
  const adContext = useContext(AdvertiserContext);

  return (
    <StyledList>
      {showCampaignsLink && (
        <StyledListItem
          component={NavLink}
          to={RoutePaths.CAMPAIGNS}
          disableRipple
          activeClassName="active"
          appTheme={adContext.theme}
        >
          <TVIcon />
          <Typography
            variant="inherit"
            color="inherit"
            data-testid="campaigns-menu-tab"
          >
            Campaigns
          </Typography>
        </StyledListItem>
      )}

      {showReportsLink && (
        <StyledListItem
          component={NavLink}
          to={RoutePaths.REPORTS}
          disableRipple
          activeClassName="active"
          appTheme={adContext.theme}
        >
          <ReportsIcon />
          <Typography
            variant="inherit"
            color="inherit"
            data-testid="reports-menu-tab"
          >
            Reports
          </Typography>
        </StyledListItem>
      )}

      {showCreativesLink && (
        <StyledListItem
          component={NavLink}
          to={{
            pathname: RoutePaths.CREATIVES,
            state: { key: uniqueId() },
          }}
          disableRipple
          activeClassName="active"
          appTheme={adContext.theme}
        >
          <CreativeIcon />
          <Typography
            variant="inherit"
            color="inherit"
            data-testid="creative-menu-tab"
          >
            Creative
          </Typography>
        </StyledListItem>
      )}

      {showTrackingLink && (
        <StyledListItem
          component={NavLink}
          to={RoutePaths.TRACKING}
          disableRipple
          activeClassName="active"
          appTheme={adContext.theme}
        >
          <TrackingIcon />
          <Typography
            variant="inherit"
            color="inherit"
            data-testid="tracking-menu-tab"
          >
            Tracking
          </Typography>
        </StyledListItem>
      )}
    </StyledList>
  );
};

export const SecondaryListItems: React.FC<SecondaryListItemsProps> = ({
  showBusinessManagerLink,
}) => {
  const adContext = useContext(AdvertiserContext);

  return (
    <StyledList>
      {showBusinessManagerLink && (
        <StyledListItem
          component={NavLink}
          to={RoutePaths.MANAGER}
          disableRipple
          activeClassName="active"
          appTheme={adContext.theme}
        >
          <ManagerIcon />
          <Typography
            variant="inherit"
            color="inherit"
            data-testid="manager-menu-tab"
          >
            Manager
          </Typography>
        </StyledListItem>
      )}
    </StyledList>
  );
};
